import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getBaseUrl } from "@/lib/site";
import { setSentryUser } from "@/lib/sentry";

interface SignUpResult {
  error: Error | null;
  data: { user: User | null } | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, meta?: Record<string, any>) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const welcomeEmailAttemptedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Update Sentry user context
        if (session?.user) {
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.name,
          });
        } else {
          setSentryUser(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Update Sentry user context
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name,
        });
      } else {
        setSentryUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const confirmedUserId = session?.user?.id;
    const emailConfirmedAt = (session?.user as any)?.email_confirmed_at as string | undefined;

    if (!confirmedUserId || !emailConfirmedAt) return;
    if (welcomeEmailAttemptedForUserRef.current === confirmedUserId) return;

    welcomeEmailAttemptedForUserRef.current = confirmedUserId;

    supabase.functions
      .invoke("send-email", {
        body: {
          type: "welcome_account_confirmed",
          userId: confirmedUserId,
        },
      })
      .catch((error) => {
        console.warn("Failed to queue welcome email after confirmation:", error);
      });
  }, [session?.user]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, meta?: Record<string, any>): Promise<SignUpResult> => {
    // Where users should land after clicking the email confirmation link.
    const redirectUrl = `${getBaseUrl()}/app`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name: meta?.name || email.split('@')[0],
          ...(meta || {}),
        },
      },
    });
    return { 
      error: error as Error | null,
      data: data ? { user: data.user } : null
    };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
